import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { config } from './config';
import type { Profile } from './types';
import { getProfile } from './api/auth';
import { extractHashParams, replaceSafeLinksUrl } from './utils/safelinks';
import { projectId } from './config';

/**
 * Accept a team invitation
 */
const acceptInvitation = async (invitationId: string, accessToken: string) => {
  try {
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-cc125661/org/invitation/${invitationId}/accept`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Failed to accept invitation:', errorData.error);
      return;
    }

    const result = await response.json();
    console.log('✅ Invitation accepted:', result);
  } catch (err) {
    console.error('❌ Error accepting invitation:', err);
  }
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock user types for development testing
type MockUserType = 'patient' | 'patient_verified' | 'packet_verified' | 'admin' | 'clinic' | 'producer';

// Mock user for development (UUID format required for database)
const createMockUser = (userType: MockUserType = 'patient'): User => {
  const userConfig: Record<MockUserType, { id: string; email: string }> = {
    patient: {
      id: '00000000-0000-0000-0000-000000000001',
      email: 'patient@cannabias.co.uk',
    },
    patient_verified: {
      id: '00000000-0000-0000-0000-000000000002',
      email: 'verified-patient@cannabias.co.uk',
    },
    packet_verified: {
      id: '00000000-0000-0000-0000-000000000003',
      email: 'packet-verified@cannabias.co.uk',
    },
    admin: {
      id: '00000000-0000-0000-0000-000000000099',
      email: 'admin@cannabias.co.uk',
    },
    clinic: {
      id: '00000000-0000-0000-0000-000000000077',
      email: 'admin@cb1medical.com',
    },
    producer: {
      id: '00000000-0000-0000-0000-000000000088',
      email: 'producer@example.com',
    },
  };

  const cfg = userConfig[userType];
  
  return {
    id: cfg.id,
    app_metadata: {},
    user_metadata: {},
    aud: 'authenticated',
    created_at: new Date().toISOString(),
    email: cfg.email,
    email_confirmed_at: new Date().toISOString(),
    phone: '',
    confirmed_at: new Date().toISOString(),
    last_sign_in_at: new Date().toISOString(),
    role: 'authenticated',
    updated_at: new Date().toISOString(),
  } as User;
};

// Mock session for development
const createMockSession = (userType: MockUserType = 'patient'): Session => ({
  access_token: 'mock-token',
  refresh_token: 'mock-refresh',
  expires_in: 3600,
  expires_at: Date.now() / 1000 + 3600,
  token_type: 'bearer',
  user: createMockUser(userType),
} as Session);

// Mock profile for development
const createMockProfile = (userType: MockUserType = 'patient'): Profile => {
  type ProfileConfig = {
    id: string;
    email: string;
    display_name: string;
    role: 'patient' | 'packet_verified' | 'admin' | 'clinic' | 'producer';
    clinic_id: string | null;
    is_verified_patient: boolean;
  };

  const profileConfig: Record<MockUserType, ProfileConfig> = {
    patient: {
      id: '00000000-0000-0000-0000-000000000001',
      email: 'patient@cannabias.co.uk',
      display_name: 'Test Patient (Unverified)',
      role: 'patient',
      clinic_id: null,
      is_verified_patient: false,
    },
    patient_verified: {
      id: '00000000-0000-0000-0000-000000000002',
      email: 'verified-patient@cannabias.co.uk',
      display_name: 'Test Patient (Verified)',
      role: 'patient',
      clinic_id: null,
      is_verified_patient: true,
    },
    packet_verified: {
      id: '00000000-0000-0000-0000-000000000003',
      email: 'packet-verified@cannabias.co.uk',
      display_name: 'Packet Verified User',
      role: 'packet_verified',
      clinic_id: null,
      is_verified_patient: false,
    },
    admin: {
      id: '00000000-0000-0000-0000-000000000099',
      email: 'admin@cannabias.co.uk',
      display_name: 'Admin User',
      role: 'admin',
      clinic_id: null,
      is_verified_patient: false,
    },
    clinic: {
      id: '00000000-0000-0000-0000-000000000077',
      email: 'admin@cb1medical.com',
      display_name: 'CB1 Medical Admin',
      role: 'clinic',
      clinic_id: '8f5d7d05-7d72-4017-a1cf-0d6e66c40a42', // CB1 Medical clinic ID
      is_verified_patient: false,
    },
    producer: {
      id: '00000000-0000-0000-0000-000000000088',
      email: 'producer@example.com',
      display_name: 'Test Producer',
      role: 'producer',
      clinic_id: null,
      is_verified_patient: false,
    },
  };

  const cfg = profileConfig[userType];

  // Check if consent has been given this session (stored in sessionStorage)
  const hasConsented = sessionStorage.getItem(`consent_given_${cfg.id}`) === 'true';
  
  return {
    id: cfg.id,
    email: cfg.email,
    display_name: cfg.display_name,
    role: cfg.role,
    clinic_id: cfg.clinic_id,
    pharmacy_id: null,
    team_role: userType === 'clinic' ? 'owner' : null,
    last_sign_in_at: new Date().toISOString(),
    is_verified_patient: cfg.is_verified_patient,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    // Mock users check sessionStorage for consent status
    consented_at: hasConsented ? new Date().toISOString() : null,
  };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // 🔴 BUG FIX #5: Memoize fetchProfile to prevent stale closures
  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const profileData = await getProfile(userId);
      setProfile(profileData);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
    }
  }, []); // setProfile is stable from useState

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  useEffect(() => {
    // 🔧 DEVELOPMENT MODE - Use mock data (unless temporarily disabled)
    const devDisableMock = localStorage.getItem('dev_disable_mock') === 'true';
    const devAdminMode = localStorage.getItem('dev_admin_mode') === 'true';
    const devClinicMode = localStorage.getItem('dev_clinic_mode') === 'true';
    const devProducerMode = localStorage.getItem('dev_producer_mode') === 'true';
    const devPacketVerifiedMode = localStorage.getItem('dev_packet_verified_mode') === 'true';
    const devVerifiedPatientMode = localStorage.getItem('dev_verified_patient_mode') === 'true';
    
    // If mock auth is disabled, show logged out state
    if (config.devMode.enabled && devDisableMock) {
      console.log('🔧 DEV MODE: Mock auth disabled - showing logged out state');
      setUser(null);
      setSession(null);
      setProfile(null);
      setLoading(false);
      return; // Don't setup real auth or mock auth
    }
    
    if (config.devMode.enabled && config.devMode.mockAuth) {
      let userType: MockUserType = 'patient';
      
      if (devClinicMode) {
        userType = 'clinic';
        console.log('🏥 DEV MODE: Using mock CLINIC user');
      } else if (devAdminMode) {
        userType = 'admin';
        console.log('👑 DEV MODE: Using mock ADMIN user');
      } else if (devProducerMode) {
        userType = 'producer';
        console.log('🏭 DEV MODE: Using mock PRODUCER user');
      } else if (devPacketVerifiedMode) {
        userType = 'packet_verified';
        console.log('📦 DEV MODE: Using mock PACKET VERIFIED user');
      } else if (devVerifiedPatientMode) {
        userType = 'patient_verified';
        console.log('✅ DEV MODE: Using mock VERIFIED PATIENT user');
      } else {
        console.log('👤 DEV MODE: Using mock PATIENT (unverified) user');
      }
      
      const mockUser = createMockUser(userType);
      const mockSession = createMockSession(userType);
      const mockProfile = createMockProfile(userType);
      
      console.log('🔑 LOGGED IN AS:', {
        email: mockProfile.email,
        role: mockProfile.role,
        displayName: mockProfile.display_name,
        clinicId: mockProfile.clinic_id,
        isVerifiedPatient: mockProfile.is_verified_patient,
      });
      
      setUser(mockUser);
      setSession(mockSession);
      setProfile(mockProfile);
      setLoading(false);
      
      return; // Skip real auth setup
    }

    // 🚀 CRITICAL: Extract tokens from URL hash IMMEDIATELY before anything else
    const extractAndSetTokensFromUrl = async () => {
      // 🔓 SAFELINKS FIX: Unwrap Microsoft Outlook SafeLinks wrapper
      const { hash, params: hashParams, wasUnwrapped } = extractHashParams(window.location.href);
      
      if (wasUnwrapped) {
        console.log('🔓 SafeLinks detected and unwrapped - this was likely from Outlook');
        // Replace the SafeLinks URL in browser history
        replaceSafeLinksUrl(true);
      }
      
      console.log('🔍 Checking URL for auth tokens:', hash.substring(0, 100));
      
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      
      if (accessToken && refreshToken) {
        console.log('✅ Found tokens in URL hash - setting session manually');
        console.log('🔑 Access token:', accessToken.substring(0, 20) + '...');
        console.log('🔑 Refresh token:', refreshToken.substring(0, 20) + '...');
        
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (error) {
            console.error('❌ Failed to set session from URL tokens:', error);
          } else {
            console.log('✅ Session set successfully from URL tokens');
            console.log('👤 User:', data.user?.email);
            setSession(data.session);
            setUser(data.user);
            if (data.user) {
              await fetchProfile(data.user.id);
              
              // Check for pending invitation
              const pendingInvitation = localStorage.getItem('pending_invitation');
              if (pendingInvitation) {
                console.log('📧 Pending invitation detected, accepting...');
                await acceptInvitation(pendingInvitation, accessToken);
                localStorage.removeItem('pending_invitation');
                // Refresh profile to get updated clinic association
                await fetchProfile(data.user.id);
              }
            }
            setLoading(false);
            return true; // Session established
          }
        } catch (err) {
          console.error('❌ Exception setting session from tokens:', err);
        }
      }
      
      return false; // No tokens found or failed to set
    };

    // Try to extract tokens first
    extractAndSetTokensFromUrl().then((tokensFound) => {
      if (tokensFound) {
        console.log('✅ Session established from URL tokens - skipping getSession');
        return;
      }
      
      // If no tokens in URL, get existing session
      console.log('🔍 No tokens in URL - checking for existing session');
      supabase.auth.getSession()
        .then(({ data: { session } }) => {
          console.log('📦 Got session from getSession:', !!session);
          setSession(session);
          setUser(session?.user ?? null);
          if (session?.user) {
            fetchProfile(session.user.id);
          }
          setLoading(false);
        })
        .catch((error) => {
          // 🔴 BUG FIX #2: Add error handler to prevent infinite loading
          console.error('❌ Failed to get initial session:', error);
          setLoading(false);
          setUser(null);
          setSession(null);
        });
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 Auth state changed:', {
        event,
        hasSession: !!session,
        hasUser: !!session?.user,
        userEmail: session?.user?.email,
        emailConfirmed: session?.user?.email_confirmed_at ? 'yes' : 'no',
      });
      
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const handleSignOut = async () => {
    // 🔧 DEVELOPMENT MODE - Just clear state
    if (config.devMode.enabled && config.devMode.mockAuth) {
      console.log('🔧 DEV MODE: Mock sign out');
      setUser(null);
      setSession(null);
      setProfile(null);
      return;
    }
    
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        signOut: handleSignOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
